import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Moment from "react-moment";
import { toast } from "react-toastify";
import { getData } from "../../../Services/Services";
import { sopInstanceList, getThemeURL } from "../../../Services/EndPoints";

// ── Incident Lifecycle Track (map overlay) ───────────────────────────────
// A compact, horizontal version of the "Incident Lifecycle" stepper shown on
// the View Incident screen (see GisincidentManagement/ViewIncident/IncidentInfo.js).
// Rendered at the bottom of the Incident Dashboard map when an incident is
// selected. Stages light up (with a timestamp) only when a matching entry is
// found in that incident's `incidentHistoryStatus`; unreached stages stay dim.
//
// Each completed stage doubles as a "Linked Module" shortcut — identical
// behaviour to the View Incident "Quick Links" panel: clicking a stage opens
// the module the stage's history entry points at (SOP instance list, resource
// assignment list, SOP instance detail for tasks, incident list), carrying the
// matched reference id(s) so the destination can bold the relevant rows.

const parseTime = (str) => (str ? new Date(str.replace(" ", "T")) : null);
const isExceptionRemark = (remarks) => /cancel|fail/i.test(remarks || "");

const LIFECYCLE_STAGES = [
  { label: "Alert Generated", icon: "🔔", test: /alert generated/i },
  { label: "Incident Created", icon: "🚨", test: /incident created/i },
  { label: "SOP Executed", icon: "📋", test: /sop executed/i },
  { label: "Resources Deployed", icon: "🚚", test: /resources? deployed/i },
  { label: "Task In Progress", icon: "🏃", test: /task.*(in ?progress)/i },
  { label: "Task Completed", icon: "✅", test: /task completed/i },
  { label: "SOP Completed", icon: "🏁", test: /sop completed|sop cancelled/i },
  { label: "Resources Released", icon: "🔓", test: /resources? released/i },
  { label: "SOP Closed", icon: "🔒", test: /sop closed/i },
  { label: "Incident Closed", icon: "🔚", test: /incident closed/i },
];

// Where each linked module lands when a stage is clicked, keyed by the history
// entry's reference_type. Mirrors NAV_BY_REF_TYPE in IncidentInfo.js.
const NAV_BY_REF_TYPE = {
  SOP_INSTANCE: {
    path: "/Disaster-acknowledgereport/view-sop/Sop-instance-list",
    stateKey: "highlightSopIds",
  },
  RESOURCE_DEPLOY: {
    path: "/Disaster-acknowledgereport/resource-assign-list",
    stateKey: "highlightDeployIds",
  },
  INCIDENT: {
    path: "/Disaster-acknowledgereport/situation-awareness/incident-management",
    stateKey: "highlightIncidentId",
    single: true,
  },
};

// "Task In Progress / Completed" opens the SOP Instance detail view directly.
const SOP_INSTANCE_DETAIL_PATH = "/Disaster-acknowledgereport/view-sop/sops-instance";

const STATE_COLOR = {
  done: "#4ADE80",
  current: "#FBBF24",
  exception: "#F87171",
  upcoming: "#DDE4EA",
};

const IncidentLifecycleTrack = ({ incident, incidentName, onClose }) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  const history = Array.isArray(incident?.incidentHistoryStatus)
    ? incident.incidentHistoryStatus
    : [];

  const { resolved, lastMatchedIdx, matchedCount } = useMemo(() => {
    const rows = history;
    const res = LIFECYCLE_STAGES.map((stage) => {
      // Every matching history row for this stage — not just the latest — so a
      // stage that fired for several SOPs/resources carries all their ref ids.
      const matches = rows
        .filter((h) => stage.test.test(h.remarks || ""))
        .map((h) => ({ ...h, t: parseTime(h.created_on) }))
        .sort((a, b) => b.t - a.t);

      let entry = matches[0] || null;
      if (!entry && stage.label === "Incident Created" && incident?.created_on) {
        entry = { created_on: incident.created_on, remarks: "Incident Created" };
      }

      const refType = matches[0]?.reference_type || null;
      const nav = refType ? NAV_BY_REF_TYPE[refType] : null;
      const refIds = [
        ...new Set(
          matches
            .map((e) => e.reference_id)
            .filter((v) => v !== null && v !== undefined)
        ),
      ];
      const linkable =
        !!matches[0] &&
        refIds.length > 0 &&
        (!!nav || refType === "TASK_INSTANCE");

      return { ...stage, entry, refType, nav, refIds, linkable };
    });

    let lastIdx = -1;
    res.forEach((s, idx) => {
      if (s.entry) lastIdx = idx;
    });
    return {
      resolved: res,
      lastMatchedIdx: lastIdx,
      matchedCount: res.filter((s) => s.entry).length,
    };
  }, [history, incident]);

  const handleStageClick = async (stage) => {
    if (!stage.linkable) return;

    // Task instances live inside a SOP instance, and the timeline only gives us
    // the task's reference_id. Open the SOP Instance detail view directly for
    // this incident's SOP instance, carrying the task ids so its Task List can
    // expand + bold the matching task rows.
    if (stage.refType === "TASK_INSTANCE") {
      const sopIds = [
        ...new Set(
          history
            .filter((h) => h.reference_type === "SOP_INSTANCE")
            .map((h) => h.reference_id)
            .filter((v) => v !== null && v !== undefined)
        ),
      ].map((v) => String(v));

      let sopInstance = null;
      try {
        const profile = await getData(getThemeURL);
        const stateId = profile?.[0]?.state_id;
        const allInstances = await getData(`${sopInstanceList}/${stateId}`);
        sopInstance = (allInstances || []).find((si) =>
          sopIds.includes(String(si.id))
        );
      } catch (e) {
        // fall through to the id-only fallback below
      }

      if (!sopInstance && sopIds.length === 0) {
        toast.error("Could not resolve the SOP instance for this task.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2500,
        });
        return;
      }

      navigate(SOP_INSTANCE_DETAIL_PATH, {
        state: {
          data: sopInstance || { id: sopIds[0] },
          highlightTaskIds: stage.refIds,
          sourceLabel: stage.label,
          fromIncident: incidentName || incident?.name || "",
        },
      });
      return;
    }

    if (!stage.nav) return;
    navigate(stage.nav.path, {
      state: {
        [stage.nav.stateKey]: stage.nav.single
          ? stage.refIds[0] ?? incident?.id
          : stage.refIds,
        sourceLabel: stage.label,
        fromIncident: incidentName || incident?.name || "",
      },
    });
  };

  if (!incident) return null;

  const linkableCount = resolved.filter((s) => s.linkable).length;

  return (
    <div
      style={{
        position: "absolute",
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 900,
        background: "rgba(255,255,255,0.97)",
        backdropFilter: "blur(6px)",
        borderRadius: 14,
        border: "1px solid rgba(42,101,145,0.14)",
        boxShadow:
          "0 2px 4px rgba(15,45,66,.06), 0 10px 32px rgba(42,101,145,.16)",
        fontFamily: "'Inter','Segoe UI',sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: "linear-gradient(135deg, #F5FBFF 0%, #EBF4FA 100%)",
          borderBottom: collapsed ? "none" : "1px solid rgba(42,101,145,0.10)",
        }}
      >
        <span style={{ fontSize: 13 }}>⚡</span>
        <span
          style={{
            fontFamily: "'Sora',sans-serif",
            fontSize: 12,
            fontWeight: 700,
            color: "#0F2D42",
          }}
        >
          Incident Lifecycle
        </span>
        {incidentName && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: "#3A5F78",
              maxWidth: 240,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            · {incidentName}
          </span>
        )}
        <span
          style={{
            marginLeft: "auto",
            fontSize: 10,
            fontWeight: 800,
            color: "#2A6591",
            background: "rgba(42,101,145,.10)",
            padding: "2px 9px",
            borderRadius: 999,
          }}
        >
          {matchedCount} / {resolved.length} stages
        </span>
        <button
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Expand" : "Collapse"}
          style={{
            width: 22,
            height: 22,
            border: "1px solid rgba(42,101,145,.16)",
            background: "#fff",
            borderRadius: 6,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            color: "#2A6591",
          }}
        >
          {collapsed ? "▲" : "▼"}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            title="Hide lifecycle"
            style={{
              width: 22,
              height: 22,
              border: "1px solid rgba(42,101,145,.16)",
              background: "#fff",
              borderRadius: 6,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              color: "#7A9BB0",
            }}
          >
            ✕
          </button>
        )}
      </div>

      {!collapsed && (
        <div style={{ padding: "12px 10px 10px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              overflowX: "auto",
              paddingBottom: 4,
            }}
          >
            {resolved.map((stage, idx) => {
              const hasEntry = !!stage.entry;
              const isException =
                hasEntry && isExceptionRemark(stage.entry.remarks);
              // "Incident Closed" is a terminal stage — always show it green
              // (done), never as the amber "current" step.
              const isTerminal = stage.label === "Incident Closed";
              let state = "upcoming";
              if (hasEntry) {
                state = isException
                  ? "exception"
                  : idx === lastMatchedIdx && !isTerminal
                  ? "current"
                  : "done";
              }
              const color = STATE_COLOR[state];
              const isHovered = hoveredIdx === idx && stage.linkable;

              return (
                <div
                  key={stage.label}
                  onClick={() => handleStageClick(stage)}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  onKeyDown={(e) => {
                    if (stage.linkable && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      handleStageClick(stage);
                    }
                  }}
                  role={stage.linkable ? "button" : undefined}
                  tabIndex={stage.linkable ? 0 : undefined}
                  title={
                    stage.linkable
                      ? `Open linked module — ${stage.label}`
                      : stage.entry?.remarks || stage.label
                  }
                  style={{
                    position: "relative",
                    flex: "1 1 0",
                    minWidth: 92,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    cursor: stage.linkable ? "pointer" : "default",
                    borderRadius: 8,
                    padding: "2px 2px 4px",
                    background: isHovered ? "rgba(42,101,145,0.06)" : "transparent",
                    transition: "background 0.15s ease",
                    outline: "none",
                  }}
                >
                  {idx > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: 19,
                        left: "-50%",
                        width: "100%",
                        height: 3,
                        background: hasEntry ? color : "#DDE4EA",
                        zIndex: 0,
                      }}
                    />
                  )}
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: "#fff",
                      border: `2px solid ${hasEntry ? color : "#DDE4EA"}`,
                      boxShadow: isHovered
                        ? "0 0 0 4px rgba(42,101,145,0.14)"
                        : state === "current"
                        ? "0 0 0 4px rgba(251,191,36,0.16)"
                        : hasEntry
                        ? "0 2px 6px rgba(16,24,40,0.08)"
                        : "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      opacity: hasEntry ? 1 : 0.45,
                      filter: hasEntry ? "none" : "grayscale(60%)",
                      transition: "box-shadow 0.15s ease",
                    }}
                  >
                    {stage.icon}
                    {hasEntry && (
                      <span
                        style={{
                          position: "absolute",
                          bottom: -2,
                          right: -2,
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          border: "2px solid #fff",
                          background: color,
                          color: "#fff",
                          fontSize: 7,
                          fontWeight: 800,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isException ? "!" : "✓"}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 9.5,
                      fontWeight: state === "current" ? 800 : 600,
                      color: stage.linkable
                        ? "#2A6591"
                        : state === "current"
                        ? "#2E6DA4"
                        : hasEntry
                        ? "#1D2939"
                        : "#8892A0",
                      lineHeight: 1.3,
                      maxWidth: 88,
                      textDecoration:
                        isHovered && stage.linkable ? "underline" : "none",
                    }}
                  >
                    {stage.label}
                    {stage.linkable && (
                      <span style={{ fontSize: 8, marginLeft: 2 }}>↗</span>
                    )}
                  </div>
                  <div
                    style={{ marginTop: 2, fontSize: 8.5, color: "#A7B1BD" }}
                  >
                    {hasEntry ? (
                      <Moment format="DD-MM-YYYY hh:mm A">
                        {stage.entry.created_on}
                      </Moment>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend + linked-module hint */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid #EEF1F4",
            }}
          >
            {[
              ["Completed", STATE_COLOR.done],
              ["Current", STATE_COLOR.current],
              ["Exception", STATE_COLOR.exception],
              ["Upcoming", "#fff"],
            ].map(([label, c]) => (
              <span
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: "#5A6B7D",
                }}
              >
                <i
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: c,
                    border: label === "Upcoming" ? "2px solid #DDE4EA" : "none",
                    display: "inline-block",
                  }}
                />
                {label}
              </span>
            ))}
            {linkableCount > 0 && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: "#2A6591",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span style={{ fontSize: 9 }}>↗</span>
                Click a stage to open its linked module
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentLifecycleTrack;
